import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Menu, Transition} from '@headlessui/react';
import {ChevronDown, Loader2} from 'lucide-react';
import clsx from 'clsx';
import {BillingType, BioMatch} from '../types';

/**
 * Combined Status + Billing selector for a BioMatch row.
 *
 * v2 model:
 *   - Curated owned recs: full dropdown — Active / Inactive +
 *     Employer 100% / split / Patient 100% billing pickers.
 *   - Range globals viewed from an org: Active / Inactive only.
 *     Globals carry no billing — they roll into the assembled Custom
 *     Lab Package, which is org-priced separately.
 *
 * "Employer / patient split" is a sticky inline row: clicking it does
 * NOT auto-commit. Admins type the percent right in the row and press
 * Apply (or Enter) to commit. Single-click for the unambiguous options.
 */

interface Props {
    rec: BioMatch;
    /**
     * Server-trusted update. The caller decides whether to PUT the rec
     * directly or call the org-billing endpoint based on `rec.organization_id`.
     */
    onChange: (next: {
        enabled: boolean;
        billing_type: BillingType;
        employer_coverage_percent: number | null;
    }) => Promise<void>;
    disabled?: boolean;
}

type SelectionId = 'inactive' | 'employer_full' | 'employer_partial' | 'patient_full';

const TONE_CLASSES: Record<string, string> = {
    inactive: 'bg-mld-danger/5 text-mld-danger hover:bg-mld-danger/10',
    employer_full: 'bg-mld-success/10 text-mld-success hover:bg-mld-success/15',
    employer_partial: 'bg-mld-info/10 text-mld-info hover:bg-mld-info/15',
    patient_full: 'bg-mld-warning/5 text-mld-warning hover:bg-mld-warning/10',
};

function activeOptionId(rec: BioMatch): SelectionId {
    if (!rec.enabled) return 'inactive';
    if (rec.billing_type === BillingType.PATIENT_FULL) return 'patient_full';
    if (rec.billing_type === BillingType.EMPLOYER_PARTIAL) return 'employer_partial';
    return 'employer_full';
}

function pillLabel(rec: BioMatch, isGlobal: boolean): string {
    if (isGlobal) {
        return rec.enabled ? 'Active' : 'Inactive';
    }
    const id = activeOptionId(rec);
    if (id === 'inactive') return 'Inactive';
    if (id === 'employer_full') return 'Employer pays 100%';
    if (id === 'patient_full') return 'Patient pays 100%';
    const pct = rec.employer_coverage_percent ?? 50;
    return `Employer ${pct}% / Patient ${100 - pct}%`;
}

const BioMatchStatusDropdown: React.FC<Props> = ({rec, onChange, disabled}) => {
    const [submitting, setSubmitting] = useState(false);
    const isGlobal = rec.organization_id == null;
    const currentId = activeOptionId(rec);

    // Local % input, scoped to the open menu. Seed from the current
    // override so re-opening shows the last-committed value.
    const [pctDraft, setPctDraft] = useState<number>(rec.employer_coverage_percent ?? 50);
    useEffect(() => {
        setPctDraft(rec.employer_coverage_percent ?? 50);
    }, [rec.employer_coverage_percent]);

    // Globals collapse to a 2-tone palette; curated recs use the full
    // status/billing palette.
    const tone = isGlobal
        ? rec.enabled
            ? TONE_CLASSES.employer_full
            : TONE_CLASSES.inactive
        : TONE_CLASSES[currentId] ?? TONE_CLASSES.employer_full;

    const commit = async (id: SelectionId, percentArg?: number) => {
        if (submitting) return;
        let next: {
            enabled: boolean;
            billing_type: BillingType;
            employer_coverage_percent: number | null;
        };
        // Global path: only `enabled` matters — the BE writes
        // biomatch_org_billing.enabled and ignores billing fields.
        if (isGlobal) {
            next = {
                enabled: id !== 'inactive',
                billing_type: rec.billing_type,
                employer_coverage_percent: null,
            };
            setSubmitting(true);
            try {
                await onChange(next);
            } finally {
                setSubmitting(false);
            }
            return;
        }
        if (id === 'inactive') {
            next = {enabled: false, billing_type: rec.billing_type, employer_coverage_percent: null};
        } else if (id === 'employer_full') {
            next = {
                enabled: true,
                billing_type: BillingType.EMPLOYER_FULL,
                employer_coverage_percent: null,
            };
        } else if (id === 'patient_full') {
            next = {
                enabled: true,
                billing_type: BillingType.PATIENT_FULL,
                employer_coverage_percent: null,
            };
        } else {
            const raw = percentArg ?? pctDraft;
            const pct = Math.max(1, Math.min(99, Math.round(Number(raw) || 0)));
            next = {
                enabled: true,
                billing_type: BillingType.EMPLOYER_PARTIAL,
                employer_coverage_percent: pct,
            };
        }
        setSubmitting(true);
        try {
            await onChange(next);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Menu as="div" className="relative inline-block text-left">
            {({close}) => (
                <>
                    <Menu.Button
                        disabled={disabled || submitting}
                        className={clsx(
                            'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition disabled:opacity-60 disabled:cursor-not-allowed',
                            tone
                        )}>
                        {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        <span>{pillLabel(rec, isGlobal)}</span>
                        {!disabled && <ChevronDown className="h-3 w-3" />}
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95">
                        <Menu.Items className="absolute right-0 z-10 mt-1 w-80 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div className="py-1">
                                {isGlobal ? (
                                    <>
                                        <SimpleOption
                                            active={rec.enabled}
                                            label="Active for this org"
                                            onSelect={() => commit('employer_full')}
                                        />
                                        <SimpleOption
                                            active={!rec.enabled}
                                            label="Inactive for this org"
                                            onSelect={() => commit('inactive')}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <SimpleOption
                                            active={currentId === 'inactive'}
                                            label="Inactive"
                                            onSelect={() => commit('inactive')}
                                        />
                                        <SimpleOption
                                            active={currentId === 'employer_full'}
                                            label="Active — Employer pays 100%"
                                            onSelect={() => commit('employer_full')}
                                        />
                                        <SplitRow
                                            isCurrent={currentId === 'employer_partial'}
                                            pct={pctDraft}
                                            onPctChange={setPctDraft}
                                            onApply={async () => {
                                                await commit('employer_partial', pctDraft);
                                                close();
                                            }}
                                        />
                                        <SimpleOption
                                            active={currentId === 'patient_full'}
                                            label="Active — Patient pays 100%"
                                            onSelect={() => commit('patient_full')}
                                        />
                                    </>
                                )}
                            </div>
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    );
};

interface SimpleOptionProps {
    active: boolean;
    label: string;
    onSelect: () => void;
}

const SimpleOption: React.FC<SimpleOptionProps> = ({active, label, onSelect}) => (
    <Menu.Item>
        {({active: hot}) => (
            <button
                type="button"
                onClick={onSelect}
                className={clsx(
                    'block w-full px-3 py-2 text-left text-xs',
                    hot ? 'bg-mld-panel-bg' : '',
                    active ? 'font-semibold text-mld-text-primary' : 'text-mld-text-secondary'
                )}>
                <div className="flex items-center justify-between">
                    <span>{label}</span>
                    {active && <CurrentBadge />}
                </div>
            </button>
        )}
    </Menu.Item>
);

interface SplitRowProps {
    isCurrent: boolean;
    pct: number;
    onPctChange: (n: number) => void;
    onApply: () => void;
}

/**
 * Sticky split row — rendered as a regular `<div>` (not a Menu.Item)
 * so clicks inside the input or label don't dismiss the menu. The
 * Apply button is the only interaction that closes + commits.
 */
const SplitRow: React.FC<SplitRowProps> = ({isCurrent, pct, onPctChange, onApply}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    return (
        <div
            className={clsx(
                'border-y border-gray-100 px-3 py-2 text-xs',
                isCurrent ? 'bg-sky-50/40' : ''
            )}>
            <div className="flex items-center justify-between">
                <span
                    className={clsx(
                        isCurrent ? 'font-semibold text-mld-text-primary' : 'text-mld-text-secondary'
                    )}>
                    Active — Employer / patient split
                </span>
                {isCurrent && <CurrentBadge />}
            </div>
            <div className="mt-1.5 flex items-center gap-2">
                <label className="text-[11px] text-gray-500">Employer covers</label>
                <input
                    ref={inputRef}
                    type="number"
                    min={1}
                    max={99}
                    step={1}
                    value={pct}
                    onChange={(e) => {
                        const n = Number(e.target.value);
                        onPctChange(Number.isFinite(n) ? n : 0);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onApply();
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-14 rounded border border-gray-300 px-2 py-1 text-xs focus:border-mld-primary focus:outline-none"
                />
                <span className="text-[11px] text-gray-500">%</span>
                <span className="text-[11px] text-gray-400">/ Patient {100 - Math.max(1, Math.min(99, pct))}%</span>
                <button
                    type="button"
                    onClick={onApply}
                    className="ml-auto rounded-md bg-mld-primary px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-mld-primary/90">
                    Apply
                </button>
            </div>
        </div>
    );
};

const CurrentBadge: React.FC = () => (
    <span className="text-mld-primary text-[10px] uppercase tracking-wide">Current</span>
);

export default BioMatchStatusDropdown;
